/** @format — Shipment status labels + badge styles (design system) */

export type ShipmentStatusCategory =
  | "pre-transit"
  | "inland-transit"
  | "issues"
  | "at-port"
  | "at-sea"
  | "at-destination"
  | "delivered"
  | "unknown";

type StatusMeta = {
  label: string;
  category: ShipmentStatusCategory;
};

const CATEGORY_COLOR: Record<ShipmentStatusCategory, string> = {
  "pre-transit": "bg-blue-50 text-blue-700",
  "inland-transit": "bg-yellow-50 text-yellow-700",
  issues: "bg-red-50 text-red-700",
  "at-port": "bg-violet-50 text-violet-700",
  "at-sea": "bg-amber-50 text-amber-700",
  "at-destination": "bg-lime-50 text-lime-700",
  delivered: "bg-green-50 text-green-700",
  unknown: "bg-neutral-50 text-neutral-600",
};

export const SHIPMENT_STATUS_BADGE_BASE =
  "inline-flex items-center justify-center gap-1 px-2 py-0.5 rounded-full border border-neutral-200 text-xs font-medium max-w-full truncate flex-shrink-0";

const STATUS_MAP: Record<string, StatusMeta> = {
  // Pre-Transit (Blue)
  booked: { label: "Booked", category: "pre-transit" },
  pending: { label: "Booked", category: "pre-transit" },
  booking_pending: { label: "Booked", category: "pre-transit" },
  booking_pending_payment: { label: "Booked", category: "pre-transit" },
  offers_received: { label: "Offers received", category: "pre-transit" },
  offer_received: { label: "Offers received", category: "pre-transit" },
  assigned: { label: "Assigned", category: "pre-transit" },
  transporter_assigned: { label: "Assigned", category: "pre-transit" },
  awaiting_assignment: { label: "Assigned", category: "pre-transit" },
  ocean_tracking_ready: { label: "Booked", category: "pre-transit" },

  // Inland Transit (Yellow)
  picked_up: { label: "Picked up", category: "inland-transit" },
  vehicle_picked_up: { label: "Picked up", category: "inland-transit" },
  in_transit_inland: { label: "In transit", category: "inland-transit" },
  in_transit: { label: "In transit", category: "inland-transit" },
  "in-transit": { label: "In transit", category: "inland-transit" },
  transporter_enroute: { label: "In transit", category: "inland-transit" },
  enroute_to_pickup: { label: "In transit", category: "inland-transit" },

  // Issues (Red)
  issues_reported: { label: "Issue reported", category: "issues" },
  issue_reported: { label: "Issue reported", category: "issues" },
  at_storage: { label: "In storage", category: "issues" },
  in_storage: { label: "In storage", category: "issues" },
  cancelled: { label: "Cancelled", category: "issues" },

  // At port (Violet)
  at_port: { label: "At origin port", category: "at-port" },
  at_origin_port: { label: "At origin port", category: "at-port" },
  clearing_customs: { label: "Clearing customs", category: "at-port" },
  loaded_on_vessel: { label: "Loaded on vessel", category: "at-port" },
  on_vessel: { label: "Loaded on vessel", category: "at-port" },

  // At sea (Amber)
  in_transit_ocean: { label: "At sea", category: "at-sea" },
  at_sea: { label: "At sea", category: "at-sea" },

  // At destination (Lime)
  arrived_destination_port: { label: "At destination port", category: "at-destination" },
  arrived_destination: { label: "At destination port", category: "at-destination" },
  ready_for_collection: { label: "Ready to collect", category: "at-destination" },
  ready_to_collect: { label: "Ready to collect", category: "at-destination" },

  // Delivered (Green)
  delivered: { label: "Delivered", category: "delivered" },
};

function normalizeStatusKey(status: string): string {
  return String(status ?? "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_");
}

function toSentenceCase(raw: string): string {
  const text = String(raw)
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase();
  if (!text) return "Unknown";
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function inferCategory(key: string): ShipmentStatusCategory {
  if (key.includes("cancel") || key.includes("issue") || key.includes("storage")) return "issues";
  if (key.includes("deliver")) return key.includes("port") || key.includes("destination") ? "at-destination" : "delivered";
  if (key.includes("destination") || key.includes("collect")) return "at-destination";
  if (key.includes("vessel") || key.includes("customs") || key.includes("origin_port") || key.includes("at_port"))
    return "at-port";
  if (key.includes("ocean") || key.includes("at_sea")) return "at-sea";
  if (key.includes("transit") || key.includes("enroute") || key.includes("picked")) return "inland-transit";
  if (key.includes("book") || key.includes("offer") || key.includes("assign") || key.includes("pending"))
    return "pre-transit";
  return "unknown";
}

export function getShipmentStatusMeta(status?: string | null) {
  const raw = String(status ?? "").trim();
  const key = normalizeStatusKey(raw);
  const hyphenKey = key.replace(/_/g, "-");
  const match = STATUS_MAP[key] ?? STATUS_MAP[hyphenKey];

  const meta: StatusMeta = match ?? {
    label: toSentenceCase(raw || "unknown"),
    category: inferCategory(key),
  };

  const colorClass = CATEGORY_COLOR[meta.category];

  return {
    label: meta.label,
    category: meta.category,
    colorClass,
    badgeClassName: `${SHIPMENT_STATUS_BADGE_BASE} ${colorClass}`,
  };
}

export function getShipmentStatusLabel(status?: string | null): string {
  return getShipmentStatusMeta(status).label;
}
